import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ShieldCheck, CheckCircle, Warning, ArrowLeft } from "@phosphor-icons/react";

const COUNTRIES = ["IN", "BD", "PK", "SG", "AE", "EG", "CH", "UK", "US", "NP", "TH"];
const TYPES = [
  { id: "counterfeit", label: "Counterfeit product" },
  { id: "trademark", label: "Trademark infringement" },
  { id: "impersonation", label: "Brand impersonation (social/web)" },
  { id: "dark_web", label: "Dark-web exposure" },
  { id: "other", label: "Other" },
];

export default function Report() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    reporter_name: "", reporter_email: "", reporter_phone: "",
    brand_name: "", country_code: "IN", incident_type: "counterfeit",
    description: "", location: "", evidence_url: "", consent_share: false,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post("/public/report", form);
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--span-bg)]" data-testid="public-report">
      <header className="border-b border-[var(--span-border)]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[13px]">
            <div className="w-7 h-7 bg-black text-white flex items-center justify-center font-display font-bold">S</div>
            <div>
              <div className="font-display font-bold leading-none">SPAN</div>
              <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--span-text-2)] mt-1">Brand Protection</div>
            </div>
          </Link>
          <Link to="/" className="text-[12px] text-[var(--span-text-2)] hover:text-black flex items-center gap-1.5">
            <ArrowLeft size={12}/> Back
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {result ? (
          <div className="card-flat p-8 text-center" data-testid="report-success">
            <CheckCircle size={48} weight="duotone" className="mx-auto text-[var(--span-success)]"/>
            <h1 className="font-display font-bold text-[24px] mt-4">{t("report.success")}</h1>
            <div className="font-mono text-[18px] mt-2">{result.case_code}</div>
            <div className="text-[12px] text-[var(--span-text-2)] mt-3 max-w-md mx-auto">
              SPAN's intake team will review your report within 48 hours. If you provided an email,
              we may contact you for additional context. Your report is confidential.
            </div>
            <Link to="/" className="span-btn span-btn-ghost mt-6 inline-flex">Return to home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="report-form">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><ShieldCheck size={12}/> Confidential intake</div>
              <h1 className="font-display font-bold text-[28px] tracking-tight mt-2">{t("report.title")}</h1>
              <p className="text-[13px] text-[var(--span-text-2)] mt-2">{t("report.subtitle")}</p>
            </div>

            <div className="card-flat p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-1">Your name *</label>
                  <input required value={form.reporter_name} onChange={(e) => set("reporter_name", e.target.value)} className="span-input" data-testid="reporter-name"/>
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Your email *</label>
                  <input required type="email" value={form.reporter_email} onChange={(e) => set("reporter_email", e.target.value)} className="span-input" data-testid="reporter-email"/>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-1">Phone (optional)</label>
                  <input value={form.reporter_phone} onChange={(e) => set("reporter_phone", e.target.value)} className="span-input"/>
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Country *</label>
                  <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="span-input">
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-1">Brand affected *</label>
                  <input required value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} placeholder="e.g. Bajaj, Dr Reddy's, HUL" className="span-input" data-testid="brand-name"/>
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Incident type *</label>
                  <select value={form.incident_type} onChange={(e) => set("incident_type", e.target.value)} className="span-input" data-testid="incident-type">
                    {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-1">Location (optional)</label>
                <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, market or URL" className="span-input"/>
              </div>

              <div>
                <label className="label-eyebrow block mb-1">Evidence URL (optional)</label>
                <input value={form.evidence_url} onChange={(e) => set("evidence_url", e.target.value)} placeholder="Public link to photo, video, listing" className="span-input"/>
              </div>

              <div>
                <label className="label-eyebrow block mb-1">Describe what you saw *</label>
                <textarea required minLength={20} rows={6} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What is being sold, where, by whom (if known), how often, etc." className="span-input" data-testid="report-description"/>
              </div>

              <label className="flex items-start gap-2 text-[12px] text-[var(--span-text-2)] cursor-pointer">
                <input type="checkbox" checked={form.consent_share} onChange={(e) => set("consent_share", e.target.checked)} className="mt-0.5" data-testid="consent-share"/>
                <span>I consent to my anonymized report being shared with the affected brand and relevant authorities for enforcement action.</span>
              </label>
            </div>

            {err && (
              <div className="border border-[var(--span-danger)] bg-red-50 dark:bg-red-950 text-[12px] p-3 flex items-center gap-2" data-testid="report-error">
                <Warning size={14}/> {String(err)}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.18em]">PROTECTED · HMAC-SHA256 + CHAIN OF CUSTODY</div>
              <button data-testid="submit-report-btn" disabled={busy} className="span-btn">
                {busy ? "Submitting…" : t("report.submit") + " →"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
